import { CostPriceAnalysis, CostPriceIngredient } from '../../services/costPrice/costPriceService';
import { Subrecipe, SubrecipeIngredient } from '../../services/subrecipes/subrecipesService';
import { resolveWaste } from '../wasteUtils';
import { productKey, ProductKeyFn } from './shared';
import { AllocationNeed, NeedOption, SubrecipeHop } from './allocationEngine';

/**
 * The recipe-tree walk: turn a cost-price item into the raw ingredient PRODUCTS it
 * consumes, expanding subrecipes and nested cost-price references. Extracted verbatim
 * from `utilizationReport.ts` (which still owns the report itself and re-imports these)
 * so the waste page can explode a thrown-away dish through the EXACT same logic the
 * utilization "needed" column uses — same waste% convention, same depth cap, same
 * cross-restaurant skip — with no second copy to drift.
 *
 * Behaviour is pinned by `utilizationReport.test.ts`; do not change the walk here
 * without that test going green unchanged.
 */

export const WASTE_MAX_DEPTH = 4;

/** Display identity of one product the walk reached, harvested from its ingredient row.
 *  Collected only when a caller passes `identityOut` — the report path never does. */
export interface ExplodedProductIdentity {
  platform: string;
  articleId?: string;
  productName: string;
  /** Std base unit (g/ml/p) of this product, '' when the row carries no quantityInfo. */
  stdUnit: string;
}

/** Record a leaf's identity the first time its key is seen (primaries and alternatives
 *  alike). No-op for the report path, which passes no map. */
function noteIdentity(
  out: Map<string, ExplodedProductIdentity> | undefined,
  key: string,
  row: { platform?: string; articleId?: string; productName?: string; quantityInfo?: { stdUnit: string } }
): void {
  if (!out || out.has(key)) return;
  out.set(key, {
    platform: row.platform ?? '',
    articleId: row.articleId,
    productName: row.productName ?? '',
    stdUnit: row.quantityInfo?.stdUnit ?? '',
  });
}

/** Walk a cost-price item's ingredients, emitting one allocation `need` per leaf
 *  ingredient line: `portions` = units-sold × any subrecipe/nested portion factors,
 *  with an ordered option list (primary first, then its alternatives). Every option's
 *  `quantityNeeded` is stored in its OWN std unit = base units (g/ml/p) — the same way
 *  the primary's is — so `perPortionBase = quantityNeeded × (1 + waste%/100)` for primary
 *  and alternatives alike; an alternative applies its OWN waste% (default fallback, never
 *  the primary's). Cross-restaurant-sourced ingredients are skipped.
 *
 *  `subrecipeChain` carries the prep hops WITH their numbers (`used` / `batchYield`), not
 *  just names: the ratio it records per hop is exactly the factor folded into `multiplier`
 *  here, which is what lets the drawers show the prep arithmetic instead of only its
 *  flattened result. Leaf needs still expose plain `subrecipes` names alongside it, so
 *  every existing consumer (recipe filter, allocation attribution) is untouched. */
export function accumulateDishIngredients(
  ingredients: CostPriceIngredient[],
  multiplier: number,
  dishName: string,
  subrecipesById: Map<string, Subrecipe>,
  defaultWaste: number,
  restId: string,
  needs: AllocationNeed[],
  cpById: Map<string, CostPriceAnalysis>,
  visitedSub: Set<string>,
  visitedCp: Set<string>,
  subrecipeChain: SubrecipeHop[],
  depth: number,
  keyOf: ProductKeyFn,
  identityOut?: Map<string, ExplodedProductIdentity>
): void {
  if (depth > WASTE_MAX_DEPTH) return;
  for (const ing of ingredients) {
    if (ing.sourceRestaurantId && ing.sourceRestaurantId !== restId) continue;

    if (ing.subrecipeId) {
      const sub = subrecipesById.get(ing.subrecipeId);
      if (!sub || !sub.portions || visitedSub.has(sub.id)) continue;
      const used = ing.subrecipePortionsNeeded ?? 0;
      const subMult = multiplier * (used / sub.portions);
      if (subMult === 0) continue;
      accumulateSubrecipeIngredients(
        sub.ingredients,
        subMult,
        dishName,
        subrecipesById,
        defaultWaste,
        needs,
        new Set(visitedSub).add(sub.id),
        [...subrecipeChain, { name: sub.name, used, batchYield: sub.portions, unit: sub.portionsUnit }],
        depth + 1,
        keyOf,
        identityOut
      );
      continue;
    }

    if (ing.nestedCostPriceId) {
      const nested = cpById.get(ing.nestedCostPriceId);
      if (!nested || visitedCp.has(nested.id)) continue;
      const nestMult = multiplier * (ing.nestedQuantityNeeded ?? 0);
      if (nestMult === 0) continue;
      accumulateDishIngredients(
        nested.ingredients,
        nestMult,
        dishName,
        subrecipesById,
        defaultWaste,
        restId,
        needs,
        cpById,
        visitedSub,
        new Set(visitedCp).add(nested.id),
        subrecipeChain,
        depth + 1,
        keyOf,
        identityOut
      );
      continue;
    }

    if (ing.quantityNeeded === undefined || !ing.platform) continue;
    const primaryKey = keyOf(ing.platform, ing.articleId, ing.productName);
    if (!primaryKey) continue;
    noteIdentity(identityOut, primaryKey, ing);
    const options: NeedOption[] = [
      { key: primaryKey, perPortionBase: ing.quantityNeeded * (1 + resolveWaste(ing.wastePercentage, defaultWaste) / 100) },
    ];
    for (const alt of ing.alternatives ?? []) {
      const altKey = keyOf(alt.platform, alt.articleId, alt.productName);
      if (!altKey) continue;
      noteIdentity(identityOut, altKey, alt);
      // alt.quantityNeeded is already in its own std unit = base units (like the primary);
      // only its OWN waste% applies (default fallback, never the primary's).
      options.push({
        key: altKey,
        perPortionBase: alt.quantityNeeded * (1 + resolveWaste(alt.wastePercentage, defaultWaste) / 100),
      });
    }
    needs.push(pushableNeed(multiplier, options, dishName, subrecipeChain));
  }
}

/** Leaf need with both chain views: plain names for every existing consumer, and the
 *  numbered hops the drawers replay. One builder so the two walks cannot drift. */
function pushableNeed(
  portions: number,
  options: NeedOption[],
  dishName: string,
  subrecipeChain: SubrecipeHop[]
): AllocationNeed {
  return {
    portions,
    options,
    dishName,
    subrecipes: subrecipeChain.map((h) => h.name),
    subrecipeHops: subrecipeChain,
  };
}

/** Subrecipe twin of accumulateDishIngredients — quantities are for the full
 *  yield, so the caller's `multiplier` already carries portionsNeeded / portions. */
export function accumulateSubrecipeIngredients(
  ingredients: SubrecipeIngredient[],
  multiplier: number,
  dishName: string,
  subrecipesById: Map<string, Subrecipe>,
  defaultWaste: number,
  needs: AllocationNeed[],
  visitedSub: Set<string>,
  subrecipeChain: SubrecipeHop[],
  depth: number,
  keyOf: ProductKeyFn,
  identityOut?: Map<string, ExplodedProductIdentity>
): void {
  if (depth > WASTE_MAX_DEPTH) return;
  for (const ing of ingredients) {
    if (ing.nestedSubrecipeId) {
      const child = subrecipesById.get(ing.nestedSubrecipeId);
      if (!child || !child.portions || visitedSub.has(child.id)) continue;
      const used = ing.nestedSubrecipePortionsNeeded ?? 0;
      const childMult = multiplier * (used / child.portions);
      if (childMult === 0) continue;
      accumulateSubrecipeIngredients(
        child.ingredients,
        childMult,
        dishName,
        subrecipesById,
        defaultWaste,
        needs,
        new Set(visitedSub).add(child.id),
        [...subrecipeChain, { name: child.name, used, batchYield: child.portions, unit: child.portionsUnit }],
        depth + 1,
        keyOf,
        identityOut
      );
      continue;
    }
    if (ing.quantityNeeded === undefined || !ing.platform) continue;
    const primaryKey = keyOf(ing.platform, ing.articleId, ing.productName);
    if (!primaryKey) continue;
    noteIdentity(identityOut, primaryKey, ing);
    const options: NeedOption[] = [
      { key: primaryKey, perPortionBase: ing.quantityNeeded * (1 + resolveWaste(ing.wastePercentage, defaultWaste) / 100) },
    ];
    for (const alt of ing.alternatives ?? []) {
      const altKey = keyOf(alt.platform, alt.articleId, alt.productName);
      if (!altKey) continue;
      noteIdentity(identityOut, altKey, alt);
      // alt.quantityNeeded is already in its own std unit = base units (like the primary);
      // only its OWN waste% applies (default fallback, never the primary's).
      options.push({
        key: altKey,
        perPortionBase: alt.quantityNeeded * (1 + resolveWaste(alt.wastePercentage, defaultWaste) / 100),
      });
    }
    needs.push(pushableNeed(multiplier, options, dishName, subrecipeChain));
  }
}

// --- prep-path grouping (drawer breakdowns) -------------------------------

/**
 * One distinct route a dish takes to reach a product: the prep hops it passed through and
 * what they work out to per ONE portion of the dish.
 *
 * Each hop's `used / batchYield` is the SHARE of that prep the dish consumes, and the
 * product of those shares is what turned a batch-sized recipe row into `perPortionBase`.
 * Deliberately no batch quantity here: the drawers state the share, not the raw prep row,
 * because that raw number (the "4 000 g" of the original bug report) is exactly what made
 * the card unreadable in the first place.
 */
export interface NeedPath {
  /** Prep hops, dish→inward. Empty = the dish lists the product directly. */
  hops: SubrecipeHop[];
  /** Base units (g/ml/p) this route contributes per ONE portion of the dish. */
  perPortionBase: number;
}

/** The fraction of the outermost prep batch that reaches one portion of the dish — the
 *  product of every hop's share. 1 for a direct route (no prep in between). */
export function needPathShare(path: NeedPath): number {
  return path.hops.reduce((f, h) => (h.batchYield > 0 ? f * (h.used / h.batchYield) : 0), 1);
}

/** Routes that took the identical hop chain are one path. */
function pathSignature(hops: SubrecipeHop[]): string {
  return hops.map((h) => `${h.name} ${h.used} ${h.batchYield} ${h.unit}`).join('');
}

/**
 * Fold one leaf's contribution into a per-route map. `contribution` lands in
 * `perPortionBase` in WHATEVER scale the caller is accumulating — the leakage walk already
 * holds per-portion amounts, the utilization walk holds period totals and normalises by
 * units sold afterwards (`scaleNeedPaths`). Contributions accumulate, so a prep listing the
 * product on two rows becomes one path, not two near-duplicate lines.
 */
export function addNeedPath(
  into: Map<string, NeedPath>,
  hops: SubrecipeHop[] | undefined,
  contribution: number
): void {
  const chain = hops ?? [];
  const sig = pathSignature(chain);
  const cur = into.get(sig);
  if (cur) {
    cur.perPortionBase += contribution;
    return;
  }
  into.set(sig, { hops: chain, perPortionBase: contribution });
}

/** Normalise accumulated period totals down to per-one-portion amounts. A zero divisor
 *  yields zeroed routes rather than Infinity — the card shows no sales to divide by in
 *  that case anyway. */
export function scaleNeedPaths(paths: NeedPath[], divisor: number): NeedPath[] {
  return paths.map((p) => ({ ...p, perPortionBase: divisor > 0 ? p.perPortionBase / divisor : 0 }));
}

/**
 * Display-ready paths, biggest contributor first — or NOTHING when every route is direct.
 * The empty case is the guarantee that a dish with no preps renders exactly the card it
 * rendered before this breakdown existed; keeping that rule in one place is why both
 * drawers call this instead of deciding for themselves.
 */
export function finalizeNeedPaths(paths: Map<string, NeedPath>): NeedPath[] {
  const list = Array.from(paths.values());
  if (!list.some((p) => p.hops.length > 0)) return [];
  return list.sort((a, b) => b.perPortionBase - a.perPortionBase);
}

// --- single-dish explosion (waste page) -----------------------------------

/** One raw product ONE portion of a dish consumes, per the recipe tree. */
export interface ExplodedProductNeed {
  /** Canonical aggregation key (`productKey`), or whatever `opts.keyOf` builds. */
  productKey: string;
  platform: string;
  articleId?: string;
  productName: string;
  /** Base units (g/ml/p) per ONE portion of the dish, waste% already applied. */
  perDishBase: number;
  /** Std base unit of this product ('' when the ingredient row has no quantityInfo). */
  stdUnit: string;
}

export interface ExplodeDishOptions {
  /** Restaurant-wide waste % fallback for ingredients without their own. */
  defaultWastePercentage: number;
  /** Ingredients sourced from another restaurant are skipped (same as utilization). */
  restaurantId: string;
  /** All analyses, so nested cost-price references resolve. Omit → nested refs stop. */
  costPriceById?: Map<string, CostPriceAnalysis>;
  /** Aggregation key builder. Defaults to the report's `productKey`. */
  keyOf?: ProductKeyFn;
}

/**
 * Explode ONE dish into the ingredient products a single portion consumes — the waste
 * page's dish→product breakdown. Runs the same walk `buildNeededIndex` runs, with the
 * sales multiplier fixed at 1, so a thrown-away dish is attributed exactly the amount
 * the utilization report would have called "needed" for that portion (per-portion base
 * × (1 + waste%), yield loss included — a made dish consumed the fully-prepped amount).
 *
 * Alternatives are NOT pooled here: without a purchase context there is no supply to
 * allocate against, so each leaf resolves to its primary option — the same rule
 * `buildPerUnitNeeds` documents for consumers without a purchase context.
 */
export function explodeDishToProductNeeds(
  costPrice: CostPriceAnalysis,
  subrecipesById: Map<string, Subrecipe>,
  opts: ExplodeDishOptions
): ExplodedProductNeed[] {
  const needs: AllocationNeed[] = [];
  const identities = new Map<string, ExplodedProductIdentity>();
  accumulateDishIngredients(
    costPrice.ingredients,
    1,
    costPrice.costPriceName,
    subrecipesById,
    opts.defaultWastePercentage,
    opts.restaurantId,
    needs,
    opts.costPriceById ?? new Map(),
    new Set(),
    // Seed the visited set with the dish itself: it is the root here (unlike the report
    // walk, where the root can never be re-entered), so a self-reference can't double-count.
    new Set([costPrice.id]),
    [],
    0,
    opts.keyOf ?? productKey,
    identities
  );

  return collapseToPrimaryProducts(needs, identities);
}

/** Collapse the walk's leaf needs onto one entry per PRIMARY product key, carrying the
 *  identity harvested for it. Alternatives are not pooled: without a purchase context
 *  there is no supply to allocate against (see `explodeDishToProductNeeds`). */
function collapseToPrimaryProducts(
  needs: AllocationNeed[],
  identities: Map<string, ExplodedProductIdentity>
): ExplodedProductNeed[] {
  const byKey = new Map<string, number>();
  for (const need of needs) {
    const primary = need.options[0];
    if (!primary) continue;
    byKey.set(primary.key, (byKey.get(primary.key) ?? 0) + need.portions * primary.perPortionBase);
  }

  return Array.from(byKey.entries()).map(([key, perDishBase]) => {
    const identity = identities.get(key);
    return {
      productKey: key,
      platform: identity?.platform ?? '',
      articleId: identity?.articleId,
      productName: identity?.productName ?? '',
      perDishBase,
      stdUnit: identity?.stdUnit ?? '',
    };
  });
}

// --- single-subrecipe explosion (counted preps in inventory) ---------------

/**
 * Drop every `isCustom` ingredient row. A custom ingredient inside a prep explodes to an
 * `<articleId>-<platform>` key that can NEVER match the `custom:<id>` row the inventory
 * counts it under, so keeping it would invent a phantom product AND count the custom
 * stock a second time (spec-subrecipes-in-inventory D2). Returns the input array
 * untouched when no row is custom, so the common case allocates nothing.
 */
function keepNonCustom(ingredients: SubrecipeIngredient[]): SubrecipeIngredient[] {
  return ingredients.some((ing) => ing.isCustom) ? ingredients.filter((ing) => !ing.isCustom) : ingredients;
}

/** The same strip applied to every subrecipe the walk can descend into — a NESTED prep's
 *  custom rows are exactly as unmatchable as the root's. Identity-preserving when clean. */
function subrecipesWithoutCustomRows(subrecipesById: Map<string, Subrecipe>): Map<string, Subrecipe> {
  let changed = false;
  const out = new Map<string, Subrecipe>();
  for (const [id, sub] of Array.from(subrecipesById.entries())) {
    const kept = keepNonCustom(sub.ingredients);
    if (kept !== sub.ingredients) changed = true;
    out.set(id, kept === sub.ingredients ? sub : { ...sub, ingredients: kept });
  }
  return changed ? out : subrecipesById;
}

/**
 * Explode ONE subrecipe (prep) into the raw ingredient products ONE portion-unit of it
 * contains — the stock side of a counted prep. `perDishBase` is therefore base units
 * (g/ml/p) per ONE portion-unit: a caller multiplies by the counted amount.
 *
 * Runs the SAME walk the "needed" column runs (`accumulateSubrecipeIngredients`), so a
 * litre of stock accounts for exactly the ingredient quantities the utilization report
 * would have charged for producing it — waste % included on leaf rows, nested preps
 * carrying none (existing, twin-consistent behaviour). Alternatives collapse to their
 * primary, as in `explodeDishToProductNeeds`.
 *
 * Callers MUST pass `opts.keyOf = inventoryKey` (`consumptionReport.ts`): the default
 * `keyOf` emits canonical `platform::identifier` keys, which match nothing in inventory
 * space. `opts.restaurantId` and `opts.costPriceById` are DEAD on this path — the
 * subrecipe walk has no cross-restaurant skip and no nested cost-price references — and
 * are only in the signature because it shares `ExplodeDishOptions`.
 *
 * Server twin: `explodeSubrecipeToProductNeeds` in `server/services/dataExportService.ts`.
 */
export function explodeSubrecipeToProductNeeds(
  subrecipe: Subrecipe,
  subrecipesById: Map<string, Subrecipe>,
  opts: ExplodeDishOptions
): ExplodedProductNeed[] {
  // Same guard every nesting site applies: a yield of 0 (or a corrupt one) has no
  // per-portion-unit content at all, and dividing by it would emit Infinity.
  const portions = subrecipe.portions;
  if (!portions || !Number.isFinite(portions) || portions <= 0) return [];

  const needs: AllocationNeed[] = [];
  const identities = new Map<string, ExplodedProductIdentity>();
  accumulateSubrecipeIngredients(
    keepNonCustom(subrecipe.ingredients),
    // Ingredient quantities are for the FULL yield, so one portion-unit is 1 / portions.
    1 / portions,
    subrecipe.name,
    subrecipesWithoutCustomRows(subrecipesById),
    opts.defaultWastePercentage,
    needs,
    // Seed the visited set with the prep itself: it is the root here, so a self-reference
    // cannot re-enter it and double-count.
    new Set([subrecipe.id]),
    [],
    0,
    opts.keyOf ?? productKey,
    identities
  );

  return collapseToPrimaryProducts(needs, identities);
}
